import classNames from 'classnames';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import DeleteIcon from '@mui/icons-material/Delete';
import EditPodIcon from '@mui/icons-material/Edit';
import CustomSelectionIcon from '@mui/icons-material/GroupAdd';
import AddAllIcon from '@mui/icons-material/PlaylistAdd';
import { makeStyles } from '@mui/styles';

import { PODShuffleStatus } from '@vestahealthcare/common/enums';
import Enum, {
  Selectable,
  SelectableInfo,
} from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import { PaginationType, PodConfig } from '@vestahealthcare/common/models';
import { LS_POD_SHUFFLE_FILTERS_V2 } from '@vestahealthcare/common/utils/constants';

import {
  Colors,
  FilterSelectable,
  IconButton,
  Panel,
  Tabs,
} from 'styleguide-v2';

import CustomSelectionModal from 'dash/src/pages/Ops/Members/CustomSelectionModal';
import { CacheServices } from 'dash/src/services';
import {
  getFilterItem,
  loadNewFiltersFromStorage,
  saveNewFiltersToStorage,
} from 'dash/src/utils/filterUtils';

import DeletePODShuffleResultsModal from './components/DeletePODShuffleModal';
import { EditPodModal } from './components/EditPODModal';
import { PODShuffleForecastTab } from './components/PODShuffleForecastTab';
import {
  PODShuffleResultsFilterBar,
  PODShuffleResultsFilters,
} from './components/PODShuffleResultsFilterBar';
import { PODShuffleResultsTab } from './components/PODShuffleResultsTab';
import { PODShuffleRosterTab } from './components/PODShuffleRosterTab';
import { PODShuffleScheduleTab } from './components/PODShuffleScheduleTab';

const POD_SHUFFLE_SCHEDULE = 'pod-shuffle-schedule';
const POD_SHUFFLE_ROSTER = 'pod-shuffle-roster';
const POD_SHUFFLE_RESULTS = 'pod-shuffle-results';
const POD_SHUFFLE_FORECAST = 'pod-shuffle-forecast';

const POD_SHUFFLE_TABS = [
  {
    value: POD_SHUFFLE_SCHEDULE,
    label: translate('pods.shuffle.schedule.title'),
  },
  {
    value: POD_SHUFFLE_ROSTER,
    label: translate('pods.shuffle.roster.title'),
  },
  {
    value: POD_SHUFFLE_RESULTS,
    label: translate('pods.shuffle.results.title'),
  },
  {
    value: POD_SHUFFLE_FORECAST,
    label: translate('pods.shuffle.forecast.title'),
  },
];

const useStyles = makeStyles({
  tabs: {
    gap: '1rem',
    '&&': {
      paddingBottom: '0.5rem',
      paddingTop: '0.5rem',
    },
    '& .fit-content': {
      minWidth: 'fit-content',
    },
  },
});

const getMonthOptions = () => {
  const options: Selectable[] = [];

  for (let i = -3; i <= 3; i += 1) {
    const monthDate = moment().add(i, 'months').startOf('month');
    options.push({
      label: monthDate.format('MMMM YYYY'),
      value: monthDate.valueOf(),
    });
  }

  return options;
};

const uniq = (array: any[]) => [...new Set(array)];

export const PODsShuffleDashboard = () => {
  const styles = useStyles();
  const [selectedTab, setSelectedTab] = useState<string>(POD_SHUFFLE_SCHEDULE);

  const [selection, setSelection] = useState<number[]>([]);
  const [memberIds, setMemberIds] = useState<number[]>([]);
  const [openEditPodModal, setOpenEditPodModal] = useState<boolean>(false);
  const [openCustomSelectionModal, setOpenCustomSelectionModal] = useState<
    boolean
  >(false);
  const [refresh, setRefresh] = useState<number>(1);
  const [refreshResults, setRefreshResults] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);

  const [resultsSelection, setResultsSelection] = useState<number[]>([]);
  const [deleteResultsOpen, setDeleteResultsOpen] = useState(false);
  const [allSelected, setAllSelected] = useState(false);
  const [hasResultsMembers, setHasResultsMembers] = useState(false);
  const [resultsPagination, setResultsPagination] = useState<PaginationType>();
  const [resultsFilters, setResultsFilters] = useState<
    PODShuffleResultsFilters
  >({});
  const [podConfigs, setPodConfigs] = useState<SelectableInfo<PodConfig>[]>([]);

  const hasSelection = !!selection.length;
  const hasMembers = !!memberIds.length;
  const hasResultsSelection = !!resultsSelection.length;
  const monthOptions = useMemo(() => getMonthOptions(), []);

  const storedFiltersConfiguration = useMemo(() => {
    if (!monthOptions.length || !podConfigs.length) return undefined;
    return {
      month: {
        data: monthOptions,
        label: translate('pods.shuffle.results.month'),
        default: monthOptions.find(
          (option) =>
            option.value ===
            moment().add(1, 'month').startOf('month').valueOf(),
        ),
      },
      podId: {
        data: podConfigs,
        label: translate('pods.shuffle.results.pod'),
      },
      status: {
        data: Enum.toSelectable(PODShuffleStatus.asArray),
        label: translate('pods.shuffle.results.status'),
      },
    };
  }, [monthOptions, podConfigs]);

  useEffect(() => {
    if (storedFiltersConfiguration) {
      const stored = loadNewFiltersFromStorage(
        LS_POD_SHUFFLE_FILTERS_V2,
        storedFiltersConfiguration,
      );

      if (stored && Object.keys(stored).length > 0) {
        setResultsFilters(stored);
      }
    }
  }, [storedFiltersConfiguration]);

  useEffect(() => {
    if (resultsFilters && Object.keys(resultsFilters).length > 0) {
      saveNewFiltersToStorage(LS_POD_SHUFFLE_FILTERS_V2, resultsFilters);
    }
  }, [resultsFilters]);

  const fetchPodConfigs = async () => {
    try {
      setLoading(true);
      const configs = await CacheServices.getPodConfigs();
      const activePods = configs.filter((pod) => pod.active);
      setPodConfigs(PodConfig.toSelectable(activePods));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPodConfigs();
  }, []);

  const updateSelectionList = () => {
    const isEdit = selectedTab === POD_SHUFFLE_RESULTS;
    if (isEdit) {
      setRefreshResults(refreshResults + 1);
    } else {
      setRefresh(refresh + 1);
    }
    setResultsSelection([]);
  };

  const selectAll = () => {
    setSelection(uniq([...memberIds]));
  };

  const getFilterBar = () => {
    if (selectedTab === POD_SHUFFLE_RESULTS) {
      return (
        <PODShuffleResultsFilterBar
          data={{
            monthOptions,
            podConfigs,
            statusOptions: Enum.toSelectable(PODShuffleStatus.asArray),
          }}
          filters={resultsFilters}
          onChange={setResultsFilters}
        />
      );
    }
    return null;
  };

  const isFutureJobAllowed = () => {
    if (!resultsFilters.month?.value) return false;
    const selectedMonth = new Date(
      (resultsFilters.month.value as FilterSelectable)?.value ||
        resultsFilters.month.value,
    );
    const nowET = new Date(
      new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }),
    );
    const lastAvailableDate = new Date(
      selectedMonth.getFullYear(),
      selectedMonth.getMonth(),
      0,
    );

    if (
      nowET.getMonth() === lastAvailableDate.getMonth() &&
      nowET.getDate() === lastAvailableDate.getDate()
        ? nowET.getHours() < 22
        : nowET < lastAvailableDate
    )
      return true;

    return false;
  };

  return (
    <Panel id="pods-shuffle-section">
      <Panel.Heading title={translate('pods.shuffle.title')} filtersV2>
        {selectedTab === POD_SHUFFLE_SCHEDULE && (
          <Panel.Actions>
            <IconButton
              data-cy="pod-shuffle-custom-selection-button"
              onClick={() => setOpenCustomSelectionModal(true)}
              tooltip={translate('pods.shuffle.schedule.customSelection')}
            >
              <CustomSelectionIcon />
            </IconButton>
            <IconButton
              data-cy="pod-shuffle-edit-pod-button"
              disabled={!hasSelection || loading}
              onClick={() => setOpenEditPodModal(true)}
              tooltip={translate('pods.shuffle.schedule.editPod')}
            >
              <EditPodIcon />
            </IconButton>
          </Panel.Actions>
        )}
        {selectedTab === POD_SHUFFLE_ROSTER && (
          <Panel.Actions>
            <div className="right-side">
              <IconButton
                onClick={() =>
                  window.open(
                    'https://docs.google.com/spreadsheets/d/1POxy6XwXZGHgppsj3XjIgJgo0F9ni9HmE5SOf_3vgIM/edit',
                    '_blank',
                    'noreferrer',
                  )
                }
                tooltip={translate(`rosterIngestion.memberTemplateFile`)}
              >
                <ContactPageIcon />
              </IconButton>
            </div>
          </Panel.Actions>
        )}
        {selectedTab === POD_SHUFFLE_RESULTS && (
          <>
            <Panel.Actions>
              <IconButton
                data-cy="pod-shuffle-edit-results-button"
                tooltip={translate('pods.shuffle.results.edit')}
                disabled={
                  (!hasResultsSelection && !allSelected) ||
                  !isFutureJobAllowed()
                }
                onClick={() => setOpenEditPodModal(true)}
              >
                <EditPodIcon />
              </IconButton>
              <IconButton
                data-cy="pod-shuffle-delete-results-button"
                tooltip={translate('pods.shuffle.results.delete')}
                disabled={
                  (!hasResultsSelection && !allSelected) ||
                  !isFutureJobAllowed()
                }
                onClick={() => setDeleteResultsOpen(true)}
              >
                <DeleteIcon />
              </IconButton>
            </Panel.Actions>
            <Panel.FilterBar
              inputs={getFilterBar()}
              onDeleteFilter={(key: string) => {
                setResultsFilters({
                  ...resultsFilters,
                  [key]: undefined,
                });
              }}
              onClearFilters={() => {
                setResultsFilters({
                  month: getFilterItem(
                    translate('pods.shuffle.results.month'),
                    monthOptions.find(
                      (option) =>
                        option.value ===
                        moment().add(1, 'month').startOf('month').valueOf(),
                    ),
                  ),
                });
              }}
              chips={{
                ...resultsFilters,
                month: resultsFilters.month
                  ? {
                      ...resultsFilters.month,
                      readonly: true,
                    }
                  : undefined,
              }}
            />
          </>
        )}

        <Panel.Tabs className={classNames('flex spaced buttons', styles.tabs)}>
          <Tabs
            items={POD_SHUFFLE_TABS}
            onChange={setSelectedTab}
            value={selectedTab}
          />
          {selectedTab === POD_SHUFFLE_SCHEDULE && (
            <>
              <span className="fit-content">
                {translate('pods.shuffle.schedule.selectedMembers', {
                  count: selection.length,
                })}
              </span>
              <div className="flex gap">
                <IconButton
                  data-cy="pod-shuffle-add-all-button"
                  disabled={
                    !hasMembers ||
                    selection.length === memberIds.length ||
                    loading
                  }
                  onClick={() => selectAll()}
                  tooltip={translate('pods.shuffle.schedule.addAll')}
                >
                  <AddAllIcon />
                </IconButton>
                <IconButton
                  data-cy="pod-shuffle-clear-button"
                  disabled={!hasSelection || loading}
                  onClick={() => {
                    setSelection([]);
                    setRefresh(refresh + 1);
                  }}
                  tooltip={translate('pods.shuffle.schedule.clear')}
                >
                  <ClearIcon />
                </IconButton>
              </div>
            </>
          )}
          {selectedTab === POD_SHUFFLE_RESULTS && (
            <>
              <span className="fit-content">
                {translate('pods.shuffle.schedule.selectedMembers', {
                  count: allSelected
                    ? (resultsPagination?.total || 0) - resultsSelection.length
                    : resultsSelection.length,
                })}
              </span>
              <div className="flex gap">
                <IconButton
                  data-cy="pod-shuffle-add-all-button"
                  disabled={allSelected || loading || !hasResultsMembers}
                  onClick={() => {
                    setAllSelected(true);
                    setResultsSelection([]);
                  }}
                  tooltip={translate('pods.shuffle.schedule.addAll')}
                >
                  <AddAllIcon />
                </IconButton>
                <IconButton
                  data-cy="pod-shuffle-clear-button"
                  disabled={
                    (!resultsSelection.length && !allSelected) || loading
                  }
                  onClick={() => {
                    setAllSelected(false);
                    setResultsSelection([]);
                  }}
                  tooltip={translate('pods.shuffle.schedule.clear')}
                >
                  <ClearIcon />
                </IconButton>
              </div>
            </>
          )}
        </Panel.Tabs>
      </Panel.Heading>
      <Panel.Body
        loading={loading}
        style={
          selectedTab === POD_SHUFFLE_ROSTER
            ? {
                backgroundColor: Colors.white,
                transition: 'none!important',
              }
            : {}
        }
      >
        {selectedTab === POD_SHUFFLE_SCHEDULE && (
          <PODShuffleScheduleTab
            selection={selection}
            setSelection={setSelection}
            memberIds={memberIds}
            setMemberIds={setMemberIds}
            refresh={refresh}
            setOuterLoading={setLoading}
          />
        )}
        {selectedTab === POD_SHUFFLE_ROSTER && (
          <PODShuffleRosterTab setLoading={setLoading} />
        )}
        {selectedTab === POD_SHUFFLE_RESULTS && (
          <PODShuffleResultsTab
            filters={resultsFilters}
            refresh={refreshResults}
            setLoading={setLoading}
            selection={resultsSelection}
            setSelection={setResultsSelection}
            setHasResultsMembers={setHasResultsMembers}
            allSelected={allSelected}
            setAllSelected={setAllSelected}
            setPagination={setResultsPagination}
          />
        )}
        {selectedTab === POD_SHUFFLE_FORECAST && (
          <PODShuffleForecastTab setLoading={setLoading} />
        )}
      </Panel.Body>
      <EditPodModal
        isEdit={selectedTab === POD_SHUFFLE_RESULTS}
        onClose={() => setOpenEditPodModal(false)}
        onSubmit={updateSelectionList}
        open={openEditPodModal}
        filters={resultsFilters}
        allSelected={allSelected}
        selection={
          selectedTab === POD_SHUFFLE_SCHEDULE ? selection : resultsSelection
        }
      />
      <DeletePODShuffleResultsModal
        open={deleteResultsOpen}
        onClose={() => setDeleteResultsOpen(false)}
        selection={resultsSelection}
        onSubmit={() => {
          setRefreshResults(refreshResults + 1);
          setDeleteResultsOpen(false);
          setResultsSelection([]);
        }}
        filters={resultsFilters}
        allSelected={allSelected}
      />
      <CustomSelectionModal
        onClose={() => setOpenCustomSelectionModal(false)}
        onSubmit={(newSelection: number[]) => {
          setSelection(uniq(newSelection));
          setMemberIds(uniq(newSelection));
          setRefresh(refresh + 1);
        }}
        open={openCustomSelectionModal}
      />
    </Panel>
  );
};

export default PODsShuffleDashboard;
